const CAP47 = {
  "id": 214,
  "title": "Riesgos de Bitcoin",
  "slug": "riesgos-de-bitcoin",
  "type": "lesson",
  "category": "bitcoin-basico",
  "level": "basic",
  "preview": "Bitcoin ofrece enormes oportunidades, pero también implica riesgos tecnológicos, regulatorios y humanos que todo usuario debe conocer para operar con seguridad.",
  "sections": [
    {
      "heading": "Introducción",
      "text": [
        "Bitcoin es una innovación financiera sin precedentes, pero como cualquier tecnología emergente conlleva riesgos. Algunos están relacionados con el propio protocolo y otros dependen del comportamiento humano.",
        "Comprender estos riesgos permite utilizar Bitcoin de forma informada y responsable."
      ]
    },

    {
      "heading": "Riesgos de la tecnología y la moneda",
      "subsections": [
        {
          "subheading": "Bitcoin puede perder adopción",
          "text": [
            "Existe un riesgo teórico de que Bitcoin sea abandonado por sus usuarios, reduciendo su utilidad y efecto red.",
            "Aunque este escenario es improbable debido a su descentralización y continua mejora, no debe descartarse totalmente.",
            "Su código abierto permite adaptaciones, pero no garantiza que siempre prevalezca."
          ]
        },
        {
          "subheading": "Bitcoin aún está en fase temprana",
          "text": [
            "Pese a su estabilidad y funcionamiento impecable desde 2009, Bitcoin sigue siendo un experimento enorme a nivel social, económico y tecnológico.",
            "Esto implica incertidumbre sobre su evolución a largo plazo."
          ]
        },
        {
          "subheading": "Problemas de escalabilidad",
          "text": [
            "La red procesa alrededor de 4–7 transacciones por segundo, un límite muy bajo frente a sistemas industriales.",
            "Este problema se está abordando mediante mejoras como SegWit, Taproot, Lightning Network y sidechains.",
            "El ecosistema evoluciona constantemente para ampliar su capacidad."
          ]
        },
        {
          "subheading": "Riesgos regulatorios",
          "text": [
            "Algunos países han prohibido o restringido Bitcoin, mientras que otros lo adoptan y regulan.",
            "Los gobiernos podrían dificultar el acceso a casas de cambio, complicando adquirir o convertir criptomonedas.",
            "La tendencia global, sin embargo, apunta hacia marcos regulatorios más claros y no hacia prohibiciones absolutas."
          ]
        },
        {
          "subheading": "Volatilidad",
          "text": [
            "El precio de Bitcoin fluctúa considerablemente debido a su aún pequeña capitalización en comparación con sistemas financieros tradicionales.",
            "Estas variaciones pueden generar miedo o pánico en nuevos usuarios, aunque disminuyen conforme aumenta la adopción global."
          ]
        },
        {
          "subheading": "Ataques criptográficos desconocidos",
          "text": [
            "Existe la posibilidad teórica de descubrirse un fallo en los algoritmos criptográficos utilizados por Bitcoin.",
            "Sin embargo, los mismos estándares protegen gobiernos, bancos y telecomunicaciones.",
            "Si alguna vulnerabilidad se descubriera, la comunidad podría realizar una actualización mediante fork para mitigarla."
          ]
        },
        {
          "subheading": "Manipulación mediática",
          "text": [
            "Los medios suelen enfocar noticias negativas asociadas a Bitcoin, lo cual puede distorsionar su percepción pública.",
            "Aun así, la influencia de los medios tradicionales disminuye, mientras que comunidades y academias especializadas ganan protagonismo."
          ]
        },
        {
          "subheading": "Competencia de otras criptomonedas",
          "text": [
            "Miles de criptomonedas compiten por atención y uso real. Es posible que alguna proponga mejoras significativas.",
            "Aun así, Bitcoin mantiene ventaja por ser pionero, tener la mayor seguridad, la mayor adopción y la comunidad más robusta."
          ]
        },
        {
          "subheading": "Rechazo a aprender algo nuevo",
          "text": [
            "Algunas personas consideran Bitcoin complejo, aunque en realidad es tan sencillo de usar como una app de envío de dinero.",
            "No es necesario entender el funcionamiento técnico para operar con él, igual que no necesitas conocer SMTP para enviar un correo."
          ]
        }
      ]
    },

    {
      "heading": "Riesgos al usar Bitcoin (los más importantes)",
      "subsections": [
        {
          "subheading": "Caer en estafas",
          "text": [
            "Es el riesgo más común. Muchas estafas prometen rentabilidades irreales, minería mágica, robots de trading o inversiones garantizadas.",
            "Los esquemas Ponzi y suplantaciones de identidad son frecuentes.",
            "La regla básica es clara: nadie debe gestionar tus bitcoin por ti. Si alguien promete beneficios garantizados, probablemente sea un fraude."
          ]
        },
        {
          "subheading": "Perder acceso a tus criptomonedas",
          "text": [
            "Si pierdes tu clave privada o tu seed phrase, pierdes tus fondos para siempre.",
            "A diferencia de un banco, no existe un botón de 'recuperar contraseña'.",
            "Virus o malware pueden robar claves si el dispositivo no es seguro."
          ]
        },
        {
          "subheading": "Robo a plataformas donde guardas bitcoin",
          "text": [
            "Casos históricos como Mt. Gox demostraron que algunas plataformas pueden ser vulneradas.",
            "La recomendación general es usar exchanges regulados y wallet personales cuando sea posible."
          ]
        },
        {
          "subheading": "Riesgos fiscales",
          "text": [
            "Los beneficios obtenidos con Bitcoin deben declararse. No hacerlo puede implicar sanciones.",
            "Las pérdidas también pueden declararse para compensar impuestos."
          ]
        },
        {
          "subheading": "Comprar bitcoin asociados a actividades delictivas",
          "text": [
            "La naturaleza pública de la blockchain permite rastrear historiales.",
            "Comprar bitcoins en mercados informales puede exponerte a recibir fondos vinculados a delitos.",
            "Por ello es recomendable usar plataformas reguladas."
          ]
        },
        {
          "subheading": "Enviar criptomonedas a la dirección equivocada",
          "text": [
            "Copiar y pegar direcciones puede ser peligroso si hay malware que modifica el portapapeles.",
            "Una vez enviada una transacción, es irreversible.",
            "Siempre verifica visualmente la dirección antes de confirmar."
          ]
        },
        {
          "subheading": "Enviar bitcoin a una dirección de otra criptomoneda",
          "text": [
            "Muchas direcciones se parecen entre sí (por ejemplo Bitcoin y Bitcoin Cash).",
            "Enviar a una red incorrecta puede resultar en pérdida total de los fondos."
          ]
        },
        {
          "subheading": "Riesgos físicos: atracos o secuestros",
          "text": [
            "Presumir públicamente la cantidad de criptomonedas que posees puede hacerte objetivo de delincuentes.",
            "La discreción y protección de tu identidad financiera son fundamentales."
          ]
        }
      ]
    },

    {
      "heading": "Conclusión",
      "text": [
        "Bitcoin posee riesgos reales, como cualquier tecnología revolucionaria.",
        "Sin embargo, la mayoría de riesgos provienen del mal uso y no del protocolo en sí.",
        "La educación y la precaución son las mejores herramientas para utilizarlos de forma segura."
      ]
    }
  ]
}


export default CAP47;
